import styled from '@emotion/styled';
import { ModulesStates } from '@racemap/utilities/consts/events';
import { shuffleArray } from '@racemap/utilities/functions';
import type { SponsorLogo } from '@racemap/utilities/types/events';
import { type FC, useCallback, useEffect, useRef, useState } from 'react';
import { useElementDimensions } from 'src/lib/customHooks';
import { useSelectedEventData } from 'src/store/leaderboard/leaderboard_reducer';
import { useLeaderboardEnvironmentInfo } from './utils';

const GAP_SIZE = 12;
const FADE_DURATION = 300; // ms

export const SponsorLogos: FC = () => {
  const selectedEventData = useSelectedEventData();
  const event = selectedEventData?.eventObject;
  const sponsorModule = event?.modules.sponsor;
  const { isMobile } = useLeaderboardEnvironmentInfo();
  const containerRef = useRef<HTMLDivElement>(null);
  const { width: containerWidth } = useElementDimensions(containerRef);
  const [logoWidths, setLogoWidths] = useState<Map<string, number>>(new Map());
  const [isVisible, setIsVisible] = useState(true);
  const [displayedLogos, setDisplayedLogos] = useState<Array<SponsorLogo>>([]);

  const logos = sponsorModule?.logos ?? [];
  const logoHeight = isMobile ? 55 : 75;

  const isActive =
    sponsorModule != null && sponsorModule.state === ModulesStates.ACTIVATED && logos.length > 0;

  // Load and measure logo images to get their widths
  useEffect(() => {
    if (logos.length === 0) return;

    const measureLogos = async () => {
      const widthMap = new Map<string, number>();

      await Promise.all(
        logos.map(
          (logo) =>
            new Promise<void>((resolve) => {
              if (logo.imageUrls?.logo == null) {
                resolve();
                return;
              }
              const img = new Image();
              img.onload = () => {
                // Calculate width based on the constrained height
                const aspectRatio = img.naturalWidth / img.naturalHeight;
                const displayWidth = logoHeight * aspectRatio;
                widthMap.set(logo.id, displayWidth);
                resolve();
              };
              img.onerror = () => resolve();
              img.src = logo.imageUrls.logo;
            }),
        ),
      );

      setLogoWidths(widthMap);
    };

    measureLogos();
  }, [logos, logoHeight]);

  // Select random logos that fit within the container width
  const selectLogosThatFit = useCallback(
    (
      allLogos: ReadonlyArray<SponsorLogo>,
      availableWidth: number,
      widths: Map<string, number>,
    ): Array<SponsorLogo> => {
      if (availableWidth <= 0 || widths.size === 0) return [];

      const shuffledLogos = shuffleArray(allLogos);
      const selectedLogos: Array<SponsorLogo> = [];
      let usedWidth = 0;

      for (const logo of shuffledLogos) {
        const logoWidth = widths.get(logo.id) ?? 0;
        const widthNeeded = selectedLogos.length > 0 ? logoWidth + GAP_SIZE : logoWidth;

        if (usedWidth + widthNeeded <= availableWidth) {
          selectedLogos.push(logo);
          usedWidth += widthNeeded;
        }
      }

      return selectedLogos;
    },
    [],
  );

  // Handle logo changes with fade transition
  useEffect(() => {
    if (logos.length === 0 || logoWidths.size === 0 || containerWidth === 0) {
      setDisplayedLogos([]);
      return;
    }

    const newLogos = selectLogosThatFit(logos, containerWidth, logoWidths);

    // If we already have logos displayed, fade out first
    if (displayedLogos.length > 0) {
      setIsVisible(false);
      const timeoutId = setTimeout(() => {
        setDisplayedLogos(newLogos);
        setIsVisible(true);
      }, FADE_DURATION);
      return () => clearTimeout(timeoutId);
    }

    // First time showing logos - just show them
    setDisplayedLogos(newLogos);
    setIsVisible(true);
  }, [logos, containerWidth, logoWidths, selectLogosThatFit]);

  // Always render the container so the ref is attached and width can be measured
  // Use height 0 when not active to avoid taking up space
  return (
    <Container
      ref={containerRef}
      className="sponsor-logos-container"
      style={{ justifyContent: sponsorModule?.alignment || 'left' }}
      height={isActive ? (isMobile ? '60px' : '80px') : '0px'}
      isVisible={isVisible}
    >
      {displayedLogos.map((logo) => (
        <Logo
          href={logo.url}
          title={logo.title}
          key={logo.id}
          target="_blank"
          rel="noopener noreferrer"
          height={isMobile ? '55px' : '75px'}
        >
          <img src={logo.imageUrls?.logo} alt={logo.altText} />
        </Logo>
      ))}
    </Container>
  );
};

const Logo = styled.a<{ height: string }>`
  cursor: pointer;

  img {
    height: ${(props) => props.height};
  }
`;

const Container = styled.div<{ height: string; isVisible: boolean }>`
  display: flex;
  height: ${(props) => props.height};
  gap: 12px;
  width: 100%;
  overflow: hidden;
  opacity: ${(props) => (props.isVisible ? 1 : 0)};
  transition: opacity ${FADE_DURATION}ms ease-in-out;
`;
